/**
 * Clickstream telemetry for the Lighthouse auditor UI. Rides the existing Katal ->
 * CloudWatch publisher (src/app/util/Metrics.ts), no new SDK. Emits View, Interaction,
 * Dwell, Abandon and Complete under the "Clickstream" namespace.
 *
 * Dimension values are attached as CloudWatch dimensions (cloudWatchDimensions), never
 * baked into the metric name — baking them in would mint a new metric per value
 * (cardinality explosion). Values are sanitized to low-cardinality, non-PII tokens.
 */

import * as KatalMetrics from '@amzn/katal-metrics';
import initialMetricsPublisher from 'src/app/util/Metrics';
import {logger} from 'src/app/util/Logger';

const clickstreamPublisher = initialMetricsPublisher.newChildActionPublisherForMethod('Clickstream');

const MS_PER_SECOND = 1000;
export const EMF_NAMESPACE = 'EagleEyePortal/Clickstream';

/** Stable surface id, e.g. "LighthouseReview" or "LighthouseReview.DecisionFooter". */
export type ComponentId = string;

/** Low-cardinality dimensions. Never pass PII, reviewIds, or free text. */
export interface ClickstreamDimensions {
    /** caseType / enforcement, e.g. "ODQ" | "Reliability" | "RCA" | "ONP" */
    enforcement?: string;
    /** rendering mode: iframe | popup | standalone */
    mode?: string;
    /** outcome bucket for interactions, e.g. "Offboard" | "NoAction" | "Pause" */
    outcome?: string;
    /** tab the interaction happened on, e.g. "Review" | "Additional-1" */
    tab?: string;
}

// Fixed order keeps the emitted dimension set stable regardless of call-site key order.
const DIM_KEYS: (keyof ClickstreamDimensions)[] = ['enforcement', 'mode', 'outcome', 'tab'];

const sanitize = (value: string): string => value.replace(/[^A-Za-z0-9_-]/g, '');

// Return a publisher carrying the given dimensions as CloudWatch dimensions, or the base
// publisher when there are none.
const publisherFor = (dims?: ClickstreamDimensions): KatalMetrics.Publisher => {
    if (!dims) {
        return clickstreamPublisher;
    }
    const cloudWatchDimensions: KatalMetrics.CloudWatchDimensions = [];
    for (const key of DIM_KEYS) {
        const value = dims[key];
        if (value != null && value !== '') {
            cloudWatchDimensions.push(new KatalMetrics.Metric.String(key, sanitize(value)));
        }
    }
    if (cloudWatchDimensions.length === 0) {
        return clickstreamPublisher;
    }
    return clickstreamPublisher.newChildPublisher({cloudWatchDimensions});
};

const emit = (
    event: string,
    id: ComponentId,
    dims: ClickstreamDimensions | undefined,
    opts: {count?: number; timerMs?: number},
): void => {
    const publisher = publisherFor(dims);
    const base = `${id}.${event}`;
    if (opts.count != null) {
        publisher.publishCounter(`${base}.Count`, opts.count);
    }
    if (opts.timerMs != null && Number.isFinite(opts.timerMs) && opts.timerMs >= 0) {
        publisher.publishTimer(`${base}.Ms`, opts.timerMs);
        emitSecondsViaEmf(base, opts.timerMs, dims);
    }
};

const emitSecondsViaEmf = (base: string, timerMs: number, dims?: ClickstreamDimensions): void => {
    try {
        const dimensionValues: Record<string, string> = {};
        for (const key of DIM_KEYS) {
            const value = dims?.[key];
            if (value != null && value !== '') {
                dimensionValues[key] = sanitize(value);
            }
        }
        const metricName = `${base}.Seconds`;
        const emfLog: Record<string, any> = {
            _aws: {
                Timestamp: Date.now(),
                CloudWatchMetrics: [{
                    Namespace: EMF_NAMESPACE,
                    // One dimension set; an empty array still yields the undimensioned metric.
                    Dimensions: [Object.keys(dimensionValues)],
                    Metrics: [{Name: metricName, Unit: 'Seconds'}],
                }],
            },
            ...dimensionValues,
            [metricName]: timerMs / MS_PER_SECOND,
        };
        void logger.info(metricName, {emfLog});
    } catch (error) {
        console.error('Clickstream EMF emit failed', error);
    }
};

/** A surface became visible / mounted. */
export const recordView = (id: ComponentId, dims?: ClickstreamDimensions): void => {
    emit('View', id, dims, {count: 1});
};

/** A discrete user interaction on a control. */
export const recordInteraction = (id: ComponentId, dims?: ClickstreamDimensions): void => {
    emit('Interaction', id, dims, {count: 1});
};

/** Time a surface was engaged, in ms. Emitted on unmount / navigation away. */
export const recordDwell = (id: ComponentId, dwellMs: number, dims?: ClickstreamDimensions): void => {
    emit('Dwell', id, dims, {timerMs: dwellMs});
};

/** Surface left without reaching its terminal action (drop-off signal). Pairs with Complete. */
export const recordAbandon = (id: ComponentId, dwellMs: number, dims?: ClickstreamDimensions): void => {
    emit('Abandon', id, dims, {count: 1, timerMs: dwellMs});
};

/** Surface reached its terminal action (decision submitted). Pairs with Abandon. */
export const recordComplete = (id: ComponentId, dwellMs: number, dims?: ClickstreamDimensions): void => {
    emit('Complete', id, dims, {count: 1, timerMs: dwellMs});
};

/** A terminal action was attempted and failed. Pairs with Complete. */
export const recordFailure = (id: ComponentId, dims?: ClickstreamDimensions): void => {
    emit('Failure', id, dims, {count: 1});
};

export const Clickstream = {
    recordView,
    recordInteraction,
    recordDwell,
    recordAbandon,
    recordComplete,
    recordFailure,
};

export default Clickstream;
